import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import InvoiceForm from '../../components/Form/InvoiceForm';
import DebitNoteLetterGenerator from '../../components/HR/DebitNoteLetterGenerator';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, StatsCards, ErrorState, TabBar } from '../../components/Common';
import type { FilterConfig, StatCard } from '../../components/Common';
import { Invoice } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import {
  useInvoices,
  useCreateInvoice,
  useUpdateInvoice,
  useDeleteInvoice,
} from '../../hooks/useInvoices';
import { useClients } from '../../hooks/useClients';
import { useBranches } from '../../hooks/useBranches';
import { useInvoiceHires } from '../../hooks/useInvoiceHires';
import { useDeductions } from '../../hooks/useDeductions';
import ClientHistoryTable from './ClientHistoryTable';
import { filterInvoiceHires, buildTabCardRows, buildClientIdSet, buildClientHistoryFilterConfigs, isNoteType } from './clientHistoryHelpers';

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'invoices' | 'debit_notes'>('invoices');
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | undefined>();
  const [showLetterGenerator, setShowLetterGenerator] = useState(false);
  const [selectedInvoiceForLetter, setSelectedInvoiceForLetter] = useState<Invoice | undefined>();
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'ar'>('en');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [amountFromFilter, setAmountFromFilter] = useState<string>('');
  const [amountToFilter, setAmountToFilter] = useState<string>('');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
    populate: '*',
  }), []);

  const { data: invoices, isLoading, error, refetch } = useInvoices(queryOptions);
  const { data: clients } = useClients(queryOptions);
  const { data: branches } = useBranches(queryOptions);
  const hiresQO = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 1000 },
    populate: '*',
  }), []);
  const { data: invoiceHires = [] } = useInvoiceHires(hiresQO);
  const { data: deductions = [] } = useDeductions(hiresQO);

  const createMutation = useCreateInvoice(() => {
    setShowForm(false);
    setEditingInvoice(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateInvoice(() => {
    setShowForm(false);
    setEditingInvoice(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteInvoice(() => {
    setTimeout(() => refetch(), 100);
  });

  const getClientName = useCallback((clientId: string) => {
    if (!clients) return '-';
    const client = clients.find((c: any) => c.documentId === clientId || String(c.id) === String(clientId));
    return client ? client.name : '-';
  }, [clients]);

  const getVesselName = useCallback((vesselId: string) => {
    if (!branches) return '-';
    const vessel = branches.find((b: any) => b.documentId === vesselId || String(b.id) === String(vesselId));
    return vessel ? vessel.name : '-';
  }, [branches]);

  // Invoices may reference the client by documentId or numeric id — accept both
  const clientIds = useMemo(() => buildClientIdSet(clients || [], clientFilter), [clients, clientFilter]);

  const filteredInvoices = useMemo(() => {
    if (!invoices) return [];
    return invoices.filter(invoice => {
      const client = getClientName(invoice.clientId);
      const vessel = getVesselName(invoice.vesselId);
      const matchesSearch = !searchTerm || (
        client.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vessel.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
      const matchesClient = !clientIds || clientIds.has(String(invoice.clientId));
      const invoiceDate = new Date(invoice.issueDate);
      const matchesDateFrom = !dateFromFilter || invoiceDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || invoiceDate <= new Date(dateToFilter);
      const matchesAmountFrom = !amountFromFilter || invoice.total >= parseFloat(amountFromFilter);
      const matchesAmountTo = !amountToFilter || invoice.total <= parseFloat(amountToFilter);
      const matchesCurrency = currencyFilter === 'all' || resolveCurrencyCode(invoice.currency) === currencyFilter;
      return matchesSearch && matchesStatus && matchesClient &&
        matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo && matchesCurrency;
    });
  }, [invoices, searchTerm, statusFilter, clientIds, dateFromFilter, dateToFilter, amountFromFilter, amountToFilter, currencyFilter, getClientName, getVesselName]);

  const filteredHires = useMemo(
    () => filterInvoiceHires(invoiceHires, {
      searchTerm, statusFilter, clientIds, dateFromFilter, dateToFilter,
      amountFromFilter, amountToFilter, currencyFilter,
    }, getClientName),
    [invoiceHires, searchTerm, statusFilter, clientIds, dateFromFilter, dateToFilter,
      amountFromFilter, amountToFilter, currencyFilter, getClientName]
  );

  // Debit/credit notes get their own tab; the Invoices tab shows everything else
  const regularHires = useMemo(() => filteredHires.filter(h => !isNoteType(h.type)), [filteredHires]);
  const noteHires = useMemo(() => filteredHires.filter(h => isNoteType(h.type)), [filteredHires]);
  const tabInvoices = activeTab === 'invoices' ? filteredInvoices : [];
  const tabHires = activeTab === 'invoices' ? regularHires : noteHires;

  // Totals follow the active tab so debit/credit notes are never mixed into
  // the invoice totals — one row of cards per currency, labeled by tab
  const statRows: StatCard[][] = useMemo(() => activeTab === 'invoices'
    ? buildTabCardRows('Invoices', regularHires, filteredInvoices)
    : buildTabCardRows('Debit/Credit Notes', noteHires),
  [activeTab, regularHires, noteHires, filteredInvoices]);

  const handleEdit = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setShowForm(true);
  };

  const handleDelete = async (invoice: Invoice) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoice.id}?`)) return;
    if (!invoice?.id) return;
    try {
      await deleteMutation.execute(invoice);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  // New invoices are created on the Invoice Hire screen, not the legacy form
  const handleAdd = () => {
    navigate('/invoice-hire?new=1');
  };

  const handleFormSubmit = async (data: Partial<Invoice>) => {
    try {
      if (editingInvoice?.id) {
        const updateData: Record<string, any> = { ...data, documentId: editingInvoice.documentId };
        if (data.currency !== undefined) {
          const c = data.currency as any;
          updateData.currency = typeof c === 'object' ? (c?.documentId || c?.id) : c;
        }
        await updateMutation.execute({ id: editingInvoice.id, data: updateData });
      } else {
        const c = data.currency as any;
        const currencyVal = typeof c === 'object' ? (c?.documentId || c?.id) : (c || 'EGP');
        await createMutation.execute({
          clientId: data.clientId, vesselId: data.vesselId, foaId: data.foaId, items: data.items || [],
          subtotal: data.subtotal || 0, tax: data.tax || 0, deduction: data.deduction || 0,
          netAfterTaxAndDeduction: data.netAfterTaxAndDeduction || 0, total: data.total || 0,
          performanceBond: data.performanceBond || 0, paid: data.paid || 0, netRemaining: data.netRemaining || 0,
          currency: currencyVal, exchangeRate: data.exchangeRate, status: data.status || 'draft',
          issueDate: data.issueDate, operationDate: data.operationDate, dueDate: data.dueDate,
          description: data.description, notes: data.notes, debitNotes: data.debitNotes || []
        });
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingInvoice(undefined);
  };

  const handleGenerateDebitNoteLetter = (invoice: Invoice, language: 'en' | 'ar' = 'en') => {
    setSelectedInvoiceForLetter(invoice);
    setSelectedLanguage(language);
    setShowLetterGenerator(true);
  };

  const handleLetterGenerate = (_data: any) => { setShowLetterGenerator(false); setSelectedInvoiceForLetter(undefined); };
  const handleLetterCancel = () => { setShowLetterGenerator(false); setSelectedInvoiceForLetter(undefined); };

  const toExportRow = (invoice: Invoice) => ({
    'Invoice ID': invoice.id, 'Client': getClientName(invoice.clientId), 'Vessel/Headquarters': getVesselName(invoice.vesselId),
    'Description': invoice.description, 'Total': invoice.total, 'Currency': resolveCurrencyCode(invoice.currency),
    'Status': invoice.status, 'Issue Date': new Date(invoice.issueDate).toLocaleDateString(), 'Due Date': new Date(invoice.dueDate).toLocaleDateString()
  });
  const handleExport = () => exportToExcel(filteredInvoices.map(toExportRow), `invoices_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(filteredInvoices.map(toExportRow), 'Invoices');

  const handleClearFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setClientFilter('all');
    setDateFromFilter(''); setDateToFilter('');
    setAmountFromFilter(''); setAmountToFilter(''); setCurrencyFilter('all');
  };

  const filterConfigs: FilterConfig[] = buildClientHistoryFilterConfigs({
    statusFilter, setStatusFilter, clientFilter, setClientFilter,
    dateFromFilter, setDateFromFilter, dateToFilter, setDateToFilter,
    amountFromFilter, setAmountFromFilter, amountToFilter, setAmountToFilter,
    currencyFilter, setCurrencyFilter, clients: clients || [],
  });

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading invoices. Please try again." onRetry={() => refetch()} entityName="invoices" />;
  if (showForm) {
    return (
      <div className="space-y-6">
        <InvoiceForm invoice={editingInvoice} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }
  if (showLetterGenerator && selectedInvoiceForLetter) {
    return (
      <DebitNoteLetterGenerator
        invoice={selectedInvoiceForLetter}
        initialLanguage={selectedLanguage}
        onClose={handleLetterCancel}
        onGenerate={handleLetterGenerate}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Client History" description="View client invoice history and debit notes" />
      {statRows.map((row, i) => (
        <StatsCards key={i} stats={row} columns={4} />
      ))}
      <TabBar
        tabs={[
          { key: 'invoices', label: `Invoices (${filteredInvoices.length + regularHires.length})` },
          { key: 'debit_notes', label: `Debit / Credit Notes (${noteHires.length})` },
        ]}
        active={activeTab}
        onChange={(key) => setActiveTab(key as 'invoices' | 'debit_notes')}
      />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search invoices..."
        filters={filterConfigs}
        onClearFilters={handleClearFilters}
        totalCount={(invoices?.length || 0) + invoiceHires.length}
        filteredCount={tabInvoices.length + tabHires.length}
        entityName={activeTab === 'invoices' ? 'Invoices' : 'Debit/Credit Notes'}
        onAdd={handleAdd}
        addLabel="Add New"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <ClientHistoryTable
        invoices={tabInvoices}
        invoiceHires={tabHires}
        deductions={deductions}
        getClientName={getClientName}
        getVesselName={getVesselName}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onGenerateLetter={handleGenerateDebitNoteLetter}
      />
    </div>
  );
};

export default Invoices;
